import React, { useEffect, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  AddOutlined as AddIcon,
  CheckCircleOutline as CompleteIcon,
  ExpandMore as ExpandMoreIcon,
  PlayArrowOutlined as StartIcon,
  RefreshOutlined as RefreshIcon,
  DeleteOutline as CancelIcon,
  PhotoCameraOutlined as CameraIcon,
  DrawOutlined as SignIcon
} from '@mui/icons-material';
import api from '../api';
import { PHOTO_UPLOAD_TIMEOUT_MS, photoFormData, photoUploadErrorMessage } from '../utils/photoUpload';
import {
  INSPECTION_TYPES,
  ITEM_STATUSES,
  MAX_PHOTO_CAPTION,
  asArray,
  checklistProgress,
  formatInspectionDate,
  itemStatusColor,
  photoEntryCaption,
  photoEntryUrl,
  statusColor,
  toPhotoEntry
} from '../utils/inspections';

/**
 * Inspections (ZRF-INSP-01/02/03).
 * Live /api/inspections — checklist, Storage photo upload, Alberta dual e-sign report.
 */
const InspectionManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [inspections, setInspections] = useState([]);
  const [properties, setProperties] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingRoomId, setUploadingRoomId] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [propertyFilter, setPropertyFilter] = useState('all');

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    propertyId: '',
    unitId: '',
    type: 'routine',
    title: '',
    notes: ''
  });
  const [createUnits, setCreateUnits] = useState([]);

  const [detailOpen, setDetailOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [draftRooms, setDraftRooms] = useState([]);
  const [draftNotes, setDraftNotes] = useState('');
  const [draftPhotoUrl, setDraftPhotoUrl] = useState('');
  const [draftPhotoCaption, setDraftPhotoCaption] = useState('');
  const [signOpen, setSignOpen] = useState(false);
  const [signForm, setSignForm] = useState({
    landlordName: '',
    landlordEmail: '',
    tenants: [{ name: '', email: '' }],
    message: 'Please review and sign this inspection report.'
  });
  const [signingLink, setSigningLink] = useState('');
  const [signerLinks, setSignerLinks] = useState([]);
  const [signatureMeta, setSignatureMeta] = useState(null);
  const [signatureLoading, setSignatureLoading] = useState(false);
  const [dirty, setDirty] = useState(false);
  const dirtyRef = useRef(false);
  const draftRoomsRef = useRef([]);
  const draftNotesRef = useRef('');

  useEffect(() => {
    loadAll({ initial: true });
  }, []);

  useEffect(() => {
    dirtyRef.current = dirty;
  }, [dirty]);

  useEffect(() => {
    draftRoomsRef.current = draftRooms;
  }, [draftRooms]);

  useEffect(() => {
    draftNotesRef.current = draftNotes;
  }, [draftNotes]);

  const markDirty = () => {
    dirtyRef.current = true;
    setDirty(true);
  };

  // `initial` swaps the page for a spinner. Refreshes after a save/upload must
  // not, or the open checklist dialog unmounts and flickers.
  const loadAll = async ({ initial = false } = {}) => {
    try {
      if (initial) {
        setLoading(true);
        setError(null);
      }
      const [inspRes, propsRes, tenantsRes] = await Promise.all([
        api.get('/api/inspections'),
        api.get('/api/properties'),
        api.get('/api/tenants').catch(() => ({ data: [] }))
      ]);
      setInspections(asArray(inspRes.data));
      setProperties(asArray(propsRes.data));
      setTenants(asArray(tenantsRes.data));
    } catch (err) {
      console.error('❌ Failed to load inspections:', err);
      if (initial) {
        setError(err.response?.data?.error || 'Failed to load inspections');
        setInspections([]);
      }
    } finally {
      if (initial) setLoading(false);
    }
  };

  const getPropertyLabel = (propertyId) => {
    const p = properties.find((x) => x.id === propertyId);
    return p?.name || p?.address || propertyId || '—';
  };

  const getUnitLabel = (propertyId, unitId) => {
    if (!unitId) return 'Whole property';
    const p = properties.find((x) => x.id === propertyId);
    const unit = (p?.units || []).find((u) => u.id === unitId);
    return unit?.unitNumber || unit?.name || unitId;
  };

  const filtered = inspections.filter((row) => {
    if (statusFilter !== 'all' && String(row.status).toLowerCase() !== statusFilter) {
      return false;
    }
    if (propertyFilter !== 'all' && row.propertyId !== propertyFilter) return false;
    return true;
  });

  const openCreate = () => {
    setCreateForm({
      propertyId: '',
      unitId: '',
      type: 'routine',
      title: '',
      notes: ''
    });
    setCreateUnits([]);
    setCreateOpen(true);
  };

  const handleCreatePropertyChange = (propertyId) => {
    const p = properties.find((x) => x.id === propertyId);
    setCreateUnits(Array.isArray(p?.units) ? p.units : []);
    setCreateForm((prev) => ({ ...prev, propertyId, unitId: '' }));
  };

  const applyInspectionToDraft = (insp) => {
    setActive(insp);
    setDraftRooms(Array.isArray(insp?.rooms) ? JSON.parse(JSON.stringify(insp.rooms)) : []);
    setDraftNotes(insp?.notes || '');
    dirtyRef.current = false;
    setDirty(false);
  };

  const refreshSignatureStatus = async (inspectionId, current = null, opts = {}) => {
    if (!inspectionId) return null;
    try {
      setSignatureLoading(true);
      const res = await api.get(`/api/inspections/${inspectionId}/signature-status`);
      setSignatureMeta(res.data);
      if (res.data?.inspection) {
        if (opts.overwriteDraft || !dirtyRef.current) {
          applyInspectionToDraft(res.data.inspection);
        } else {
          // Keep local checklist drafts; only refresh signature metadata on active
          setActive((prev) => ({
            ...(prev || {}),
            ...res.data.inspection,
            rooms: prev?.rooms,
            notes: prev?.notes
          }));
        }
      }
      return res.data;
    } catch (err) {
      console.warn('⚠️ Signature status refresh failed:', err.response?.data?.error || err.message);
      if (current?.signatureRequestId || current?.reportStatus) {
        setSignatureMeta({
          signatureRequestId: current.signatureRequestId || null,
          status: current.reportStatus || null,
          signers: [],
          allSigned: current.reportStatus === 'signed',
          signedPdfUrl: current.signedReportUrl || null
        });
      }
      return null;
    } finally {
      setSignatureLoading(false);
    }
  };

  // The emailed report link closes 30 days after signing; signed in, the landlord can always open it.
  const openSignedReport = async ({ download = false } = {}) => {
    const requestId = signatureMeta?.signatureRequestId || active?.signatureRequestId;
    const storedUrl = signatureMeta?.signedPdfUrl || active?.signedReportUrl;
    if (!requestId) {
      if (storedUrl) window.open(storedUrl, '_blank', 'noopener');
      return;
    }
    const viewer = download ? null : window.open('', '_blank');
    try {
      setError(null);
      const res = await api.get(`/api/e-signature/pdf/${encodeURIComponent(requestId)}/file`, {
        responseType: 'blob'
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      if (viewer) {
        viewer.location.href = url;
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = `inspection-report-${active?.id || requestId}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      console.error('❌ Open signed report failed:', err);
      if (viewer) viewer.close();
      setError('Could not open the signed report. Please try again.');
    }
  };

  const openDetail = async (row) => {
    try {
      setError(null);
      setSignatureMeta(null);
      dirtyRef.current = false;
      setDirty(false);
      const res = await api.get(`/api/inspections/${row.id}`);
      const insp = res.data;
      applyInspectionToDraft(insp);
      setDraftPhotoUrl('');
      setDraftPhotoCaption('');
      setDetailOpen(true);
      if (insp.signatureRequestId || insp.reportStatus) {
        await refreshSignatureStatus(insp.id, insp, { overwriteDraft: true });
      }
    } catch (err) {
      console.error('❌ Load inspection failed:', err);
      setError(err.response?.data?.error || 'Failed to open inspection');
    }
  };

  const handleCreate = async () => {
    if (!createForm.propertyId) {
      setError('Select a property');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      const res = await api.post('/api/inspections', {
        propertyId: createForm.propertyId,
        unitId: createForm.unitId || null,
        type: createForm.type,
        title: createForm.title || undefined,
        notes: createForm.notes || ''
      });
      setCreateOpen(false);
      setSuccess('Inspection run created (draft)');
      await loadAll();
      await openDetail(res.data);
    } catch (err) {
      console.error('❌ Create inspection failed:', err);
      setError(err.response?.data?.error || 'Failed to create inspection');
    } finally {
      setSaving(false);
    }
  };

  const updateItemStatus = (roomId, itemId, nextStatus) => {
    markDirty();
    setDraftRooms((rooms) =>
      rooms.map((room) => {
        if (room.id !== roomId) return room;
        return {
          ...room,
          items: (room.items || []).map((item) =>
            item.id === itemId ? { ...item, status: nextStatus } : item
          )
        };
      })
    );
  };

  const updateItemNotes = (roomId, itemId, notes) => {
    markDirty();
    setDraftRooms((rooms) =>
      rooms.map((room) => {
        if (room.id !== roomId) return room;
        return {
          ...room,
          items: (room.items || []).map((item) =>
            item.id === itemId ? { ...item, notes } : item
          )
        };
      })
    );
  };

  const updateRoomNotes = (roomId, notes) => {
    markDirty();
    setDraftRooms((rooms) =>
      rooms.map((room) => (room.id === roomId ? { ...room, notes } : room))
    );
  };

  const addRoomPhotoUrl = async (roomId) => {
    const url = draftPhotoUrl.trim();
    if (!url || !active?.id) return;
    const caption = draftPhotoCaption.trim().slice(0, MAX_PHOTO_CAPTION);
    const entry = toPhotoEntry(url, caption);
    markDirty();
    setDraftRooms((rooms) =>
      rooms.map((room) => {
        if (room.id !== roomId) return room;
        const existing = Array.isArray(room.photoUrls) ? room.photoUrls : [];
        if (existing.some((p) => photoEntryUrl(p) === url)) return room;
        return { ...room, photoUrls: [...existing, entry].slice(0, 50) };
      })
    );
    setDraftPhotoUrl('');
    setDraftPhotoCaption('');
    try {
      setSaving(true);
      setError(null);
      // Persist immediately so pasted URLs are not lost on close/photo upload
      const nextRooms = draftRooms.map((room) => {
        if (room.id !== roomId) return room;
        const existing = Array.isArray(room.photoUrls) ? room.photoUrls : [];
        if (existing.some((p) => photoEntryUrl(p) === url)) return room;
        return { ...room, photoUrls: [...existing, entry].slice(0, 50) };
      });
      const res = await api.put(`/api/inspections/${active.id}`, {
        rooms: nextRooms,
        notes: draftNotes
      });
      applyInspectionToDraft(res.data);
      setSuccess('Photo URL saved');
      await loadAll();
    } catch (err) {
      console.error('❌ Save photo URL failed:', err);
      setError(err.response?.data?.error || 'Failed to save photo URL');
    } finally {
      setSaving(false);
    }
  };

  const updateRoomPhotoCaption = (roomId, photoUrl, caption) => {
    markDirty();
    const next = String(caption || '').slice(0, MAX_PHOTO_CAPTION);
    setDraftRooms((rooms) =>
      rooms.map((room) => {
        if (room.id !== roomId) return room;
        return {
          ...room,
          photoUrls: (room.photoUrls || []).map((entry) => {
            if (photoEntryUrl(entry) !== photoUrl) return entry;
            return toPhotoEntry(photoUrl, next);
          })
        };
      })
    );
  };

  const removeRoomPhoto = (roomId, photoUrl) => {
    markDirty();
    setDraftRooms((rooms) =>
      rooms.map((r) =>
        r.id === roomId
          ? {
              ...r,
              photoUrls: (r.photoUrls || []).filter((u) => photoEntryUrl(u) !== photoUrl)
            }
          : r
      )
    );
  };

  const uploadRoomPhoto = async (roomId, file) => {
    if (!active?.id || !file) return;
    try {
      setUploadingRoomId(roomId);
      setError(null);
      const roomsSnapshot = draftRoomsRef.current;
      const notesSnapshot = draftNotesRef.current;
      const captionSnapshot = draftPhotoCaption.trim().slice(0, MAX_PHOTO_CAPTION);
      // Send current checklist with the upload so the API does not overwrite
      // unsaved status/notes with the previous DB snapshot.
      const formData = await photoFormData(file, {
        roomId,
        caption: captionSnapshot || undefined,
        rooms: JSON.stringify(roomsSnapshot),
        notes: notesSnapshot == null ? '' : String(notesSnapshot)
      });
      const res = await api.post(`/api/inspections/${active.id}/photos`, formData, {
        timeout: PHOTO_UPLOAD_TIMEOUT_MS
      });
      applyInspectionToDraft(res.data);
      setDraftPhotoCaption('');
      setSuccess(captionSnapshot ? 'Photo uploaded with note' : 'Photo uploaded');
      await loadAll();
    } catch (err) {
      console.error('❌ Photo upload failed:', err);
      setError(photoUploadErrorMessage(err));
    } finally {
      setUploadingRoomId(null);
    }
  };

  const openSignDialog = () => {
    const linkedTenant = tenants.find((t) => t.id === active?.tenantId);
    setSignForm({
      landlordName: '',
      landlordEmail: '',
      tenants: [
        {
          name: linkedTenant?.name || linkedTenant?.fullName || '',
          email: linkedTenant?.email || ''
        }
      ],
      message: 'Please review and sign this inspection report.'
    });
    // Do not reopen old completed links — only show links after a fresh send.
    setSigningLink('');
    setSignerLinks([]);
    setSignOpen(true);
  };

  const handleSendForSignature = async () => {
    if (!active?.id) return;
    if (!signForm.landlordName.trim() || !signForm.landlordEmail.trim()) {
      setError('Landlord name and email are required');
      return;
    }
    const tenantPayload = (signForm.tenants || [])
      .map((t) => ({
        name: String(t.name || '').trim(),
        email: String(t.email || '').trim()
      }))
      .filter((t) => t.name && t.email);
    if (!tenantPayload.length) {
      setError('Add at least one tenant name and email');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      const res = await api.post(`/api/inspections/${active.id}/send-for-signature`, {
        landlord: { name: signForm.landlordName.trim(), email: signForm.landlordEmail.trim() },
        tenants: tenantPayload,
        // legacy single-tenant field for older API clients
        tenant: tenantPayload[0],
        message: signForm.message
      });
      setActive(res.data.inspection || active);
      setSigningLink(res.data.signingUrl || '');
      const links = (res.data.emails || [])
        .filter((e) => e.signingUrl)
        .map((e) => ({
          role: e.role || '',
          email: e.email,
          url: e.signingUrl,
          success: e.success
        }));
      setSignerLinks(links);
      const emailOk = (res.data.emails || []).filter((e) => e.success).length;
      const emailTotal = (res.data.emails || []).length;
      setSuccess(
        emailTotal
          ? `Signature invites sent (${emailOk}/${emailTotal}). Check email for each signer link.`
          : 'Inspection report sent for signature'
      );
      setSignOpen(false);
      await loadAll();
      if (res.data.inspection?.id || active?.id) {
        await refreshSignatureStatus(res.data.inspection?.id || active.id, res.data.inspection);
      }
    } catch (err) {
      console.error('❌ Send for signature failed:', err);
      setError(err.response?.data?.error || 'Failed to send for signature');
    } finally {
      setSaving(false);
    }
  };

  const saveChecklistQuiet = async () => {
    if (!active?.id) return null;
    const res = await api.put(`/api/inspections/${active.id}`, {
      rooms: draftRoomsRef.current,
      notes: draftNotesRef.current
    });
    applyInspectionToDraft(res.data);
    return res.data;
  };

  const saveChecklist = async () => {
    if (!active?.id) return;
    try {
      setSaving(true);
      setError(null);
      await saveChecklistQuiet();
      setSuccess('Checklist saved');
      await loadAll();
    } catch (err) {
      console.error('❌ Save checklist failed:', err);
      setError(err.response?.data?.error || 'Failed to save checklist');
    } finally {
      setSaving(false);
    }
  };

  const closeDetail = async () => {
    if (saving || uploadingRoomId) return;
    const canEdit =
      active && active.status !== 'completed' && active.status !== 'cancelled';
    if (canEdit && dirtyRef.current) {
      try {
        setSaving(true);
        setError(null);
        await saveChecklistQuiet();
        setSuccess('Checklist saved');
        await loadAll();
      } catch (err) {
        console.error('❌ Autosave on close failed:', err);
        setError(err.response?.data?.error || 'Failed to save checklist — keep the window open and try Save');
        setSaving(false);
        return;
      } finally {
        setSaving(false);
      }
    }
    setDetailOpen(false);
  };

  const handleStart = async () => {
    if (!active?.id) return;
    try {
      setSaving(true);
      setError(null);
      await saveChecklistQuiet();
      const res = await api.post(`/api/inspections/${active.id}/start`);
      applyInspectionToDraft(res.data);
      setSuccess('Inspection started');
      await loadAll();
    } catch (err) {
      console.error('❌ Start failed:', err);
      setError(err.response?.data?.error || 'Failed to start inspection');
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async () => {
    if (!active?.id) return;
    try {
      setSaving(true);
      setError(null);
      await saveChecklistQuiet();
      const res = await api.post(`/api/inspections/${active.id}/complete`);
      applyInspectionToDraft(res.data);
      setSuccess('Inspection marked complete');
      await loadAll();
    } catch (err) {
      console.error('❌ Complete failed:', err);
      setError(err.response?.data?.error || 'Failed to complete inspection');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async () => {
    if (!active?.id) return;
    if (!window.confirm('Cancel this inspection run?')) return;
    try {
      setSaving(true);
      setError(null);
      const res = await api.post(`/api/inspections/${active.id}/cancel`, {
        reason: 'Cancelled from Inspections screen'
      });
      setActive(res.data);
      setSuccess('Inspection cancelled');
      await loadAll();
    } catch (err) {
      console.error('❌ Cancel failed:', err);
      setError(err.response?.data?.error || 'Failed to cancel inspection');
    } finally {
      setSaving(false);
    }
  };

  const readOnly = active?.status === 'completed' || active?.status === 'cancelled';
  const progress = checklistProgress(draftRooms);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          mb: { xs: 2, sm: 3 },
          gap: 2
        }}
      >
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
            Inspections
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Room checklists for move-in, move-out, and routine runs. AI voice notes later.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' }, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => loadAll({ initial: true })}
            fullWidth={isMobile}
            size={isMobile ? 'small' : 'medium'}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreate}
            fullWidth={isMobile}
            size={isMobile ? 'small' : 'medium'}
          >
            New inspection
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="draft">Draft</MenuItem>
            <MenuItem value="in_progress">In progress</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Property</InputLabel>
          <Select
            value={propertyFilter}
            label="Property"
            onChange={(e) => setPropertyFilter(e.target.value)}
          >
            <MenuItem value="all">All properties</MenuItem>
            {properties.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.name || p.address || p.id}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {filtered.length === 0 ? (
        <Alert severity="info">
          No inspection runs yet. Create one for a property — a room checklist is generated automatically.
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {filtered.map((row) => {
            const prog = checklistProgress(row.rooms);
            return (
              <Grid item xs={12} md={6} key={row.id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                      <Typography variant="h6" sx={{ fontSize: '1.05rem' }}>
                        {row.title || 'Inspection'}
                      </Typography>
                      <Chip
                        size="small"
                        label={String(row.status || '').replace('_', ' ')}
                        color={statusColor(row.status)}
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {getPropertyLabel(row.propertyId)} · {getUnitLabel(row.propertyId, row.unitId)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Type: {String(row.type || '').replace('_', ' ')} · Checklist {prog.done}/{prog.total} (
                      {prog.percent}%)
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                      Created {formatInspectionDate(row.createdAt)}
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Button size="small" onClick={() => openDetail(row)}>
                      Open checklist
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Dialog open={createOpen} onClose={() => !saving && setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>New inspection run</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth required>
              <InputLabel>Property</InputLabel>
              <Select
                value={createForm.propertyId}
                label="Property"
                onChange={(e) => handleCreatePropertyChange(e.target.value)}
              >
                {properties.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name || p.address || p.id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Unit (optional)</InputLabel>
              <Select
                value={createForm.unitId}
                label="Unit (optional)"
                onChange={(e) => setCreateForm({ ...createForm, unitId: e.target.value })}
                disabled={!createForm.propertyId}
              >
                <MenuItem value="">Whole property</MenuItem>
                {createUnits.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.unitNumber || u.name || u.id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={createForm.type}
                label="Type"
                onChange={(e) => setCreateForm({ ...createForm, type: e.target.value })}
              >
                {INSPECTION_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Title (optional)"
              value={createForm.title}
              onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })}
              fullWidth
            />
            <TextField
              label="Notes"
              value={createForm.notes}
              onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })}
              multiline
              rows={2}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreate} disabled={saving || !createForm.propertyId}>
            {saving ? <CircularProgress size={20} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={detailOpen}
        onClose={() => {
          closeDetail();
        }}
        fullWidth
        maxWidth="md"
        fullScreen={isMobile}
      >
        <DialogTitle>
          {active?.title || 'Inspection'}
          {active?.status && (
            <Chip
              size="small"
              sx={{ ml: 1 }}
              label={String(active.status).replace('_', ' ')}
              color={statusColor(active.status)}
            />
          )}
          {dirty && !readOnly && (
            <Chip size="small" sx={{ ml: 1 }} label="Unsaved changes" color="warning" />
          )}
        </DialogTitle>
        <DialogContent dividers>
          {active && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {getPropertyLabel(active.propertyId)} · {getUnitLabel(active.propertyId, active.unitId)} ·{' '}
                {String(active.type || '').replace('_', ' ')}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Checklist progress: {progress.done}/{progress.total} ({progress.percent}%)
              </Typography>

              <Alert severity="info" sx={{ mb: 2 }}>
                Add photos with the camera/file button (stored securely). When the run is complete,
                send for Alberta dual signature (landlord + tenant).
              </Alert>

              {(active.reportStatus === 'pending_signatures' ||
                active.reportStatus === 'signed' ||
                signatureMeta?.signatureRequestId) && (
                <Alert
                  severity={
                    active.reportStatus === 'signed' || signatureMeta?.allSigned
                      ? 'success'
                      : 'warning'
                  }
                  sx={{ mb: 2 }}
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => refreshSignatureStatus(active.id, active)}
                      disabled={signatureLoading}
                      startIcon={
                        signatureLoading ? <CircularProgress size={14} /> : <RefreshIcon />
                      }
                    >
                      Refresh
                    </Button>
                  }
                >
                  {active.reportStatus === 'signed' || signatureMeta?.allSigned ? (
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        All parties have completed signing.
                      </Typography>
                      {(signatureMeta?.signers || []).length > 0 && (
                        <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                          {(signatureMeta.signers || [])
                            .map((s) => `${s.role || 'signer'}: ${s.status}`)
                            .join(' · ')}
                        </Typography>
                      )}
                      <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {(signatureMeta?.signedPdfUrl || active.signedReportUrl) && (
                          <>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => openSignedReport()}
                            >
                              View signed report
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => openSignedReport({ download: true })}
                            >
                              Download PDF
                            </Button>
                          </>
                        )}
                      </Box>
                    </Box>
                  ) : (
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Report awaiting signatures
                      </Typography>
                      {(signatureMeta?.signers || []).length > 0 ? (
                        <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                          {(signatureMeta.signers || [])
                            .map(
                              (s) =>
                                `${s.role || 'signer'} (${s.email}): ${
                                  s.status === 'signed' ? 'signed' : 'pending'
                                }`
                            )
                            .join(' · ')}
                        </Typography>
                      ) : (
                        <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                          Landlord and tenant must both sign via their email links.
                        </Typography>
                      )}
                    </Box>
                  )}
                </Alert>
              )}

              <TextField
                label="Run notes"
                value={draftNotes}
                onChange={(e) => {
                  markDirty();
                  setDraftNotes(e.target.value);
                }}
                fullWidth
                multiline
                rows={2}
                disabled={readOnly}
                sx={{ mb: 2 }}
              />

              {draftRooms.map((room, roomIndex) => (
                <Accordion key={room.id} defaultExpanded={roomIndex === 0} disableGutters>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography sx={{ flex: 1 }}>{room.name}</Typography>
                    <Chip
                      size="small"
                      label={`${(room.items || []).filter((i) => i.status !== 'pending').length}/${
                        (room.items || []).length
                      }`}
                      sx={{ mr: 1 }}
                    />
                  </AccordionSummary>
                  <AccordionDetails>
                    <TextField
                      label="Room notes"
                      value={room.notes || ''}
                      onChange={(e) => updateRoomNotes(room.id, e.target.value)}
                      fullWidth
                      size="small"
                      disabled={readOnly}
                      sx={{ mb: 1.5 }}
                    />

                    {/* Photos first in the room so evidence is visible while walking the checklist */}
                    <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
                      Photos
                    </Typography>
                    {!readOnly && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1 }}>
                        <TextField
                          size="small"
                          label="Photo note (optional)"
                          value={draftPhotoCaption}
                          onChange={(e) =>
                            setDraftPhotoCaption(e.target.value.slice(0, MAX_PHOTO_CAPTION))
                          }
                          placeholder="e.g. Scratch on cabinet door"
                          helperText={`${draftPhotoCaption.length}/${MAX_PHOTO_CAPTION} — applies to next photo or URL`}
                          fullWidth
                          multiline
                          minRows={1}
                          maxRows={2}
                        />
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                          <Button
                            size="small"
                            variant="contained"
                            component="label"
                            startIcon={
                              uploadingRoomId === room.id ? (
                                <CircularProgress size={14} color="inherit" />
                              ) : (
                                <CameraIcon />
                              )
                            }
                            disabled={Boolean(uploadingRoomId)}
                          >
                            Add photo
                            <input
                              hidden
                              type="file"
                              accept="image/*"
                              capture="environment"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                e.target.value = '';
                                if (file) uploadRoomPhoto(room.id, file);
                              }}
                            />
                          </Button>
                          <TextField
                            size="small"
                            label="Or paste photo URL"
                            value={draftPhotoUrl}
                            onChange={(e) => setDraftPhotoUrl(e.target.value)}
                            sx={{ flex: 1, minWidth: 160 }}
                            placeholder="https://…"
                          />
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => addRoomPhotoUrl(room.id)}
                            disabled={!draftPhotoUrl.trim()}
                          >
                            Add URL
                          </Button>
                        </Box>
                      </Box>
                    )}
                    {(room.photoUrls || []).length > 0 ? (
                      <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                        {room.photoUrls.map((entry) => {
                          const url = photoEntryUrl(entry);
                          if (!url) return null;
                          const caption = photoEntryCaption(entry);
                          return (
                            <Box
                              key={url}
                              sx={{
                                display: 'flex',
                                gap: 1,
                                alignItems: 'flex-start',
                                flexWrap: 'wrap'
                              }}
                            >
                              <Box sx={{ position: 'relative', flexShrink: 0 }}>
                                <Box
                                  component="a"
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  aria-label={
                                    caption
                                      ? `Open photo: ${caption}`
                                      : `Open ${room.name || 'room'} photo`
                                  }
                                  sx={{
                                    display: 'block',
                                    width: 72,
                                    height: 72,
                                    borderRadius: 1,
                                    overflow: 'hidden',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    backgroundImage: `url(${url})`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center'
                                  }}
                                />
                                {!readOnly && (
                                  <IconButton
                                    size="small"
                                    aria-label="Remove photo"
                                    onClick={() => removeRoomPhoto(room.id, url)}
                                    sx={{
                                      position: 'absolute',
                                      top: -8,
                                      right: -8,
                                      bgcolor: 'background.paper',
                                      boxShadow: 1
                                    }}
                                  >
                                    <CancelIcon fontSize="inherit" />
                                  </IconButton>
                                )}
                              </Box>
                              {readOnly ? (
                                caption ? (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ flex: 1, minWidth: 140, pt: 0.5 }}
                                  >
                                    {caption}
                                  </Typography>
                                ) : null
                              ) : (
                                <TextField
                                  size="small"
                                  label="Photo note"
                                  value={caption}
                                  onChange={(e) =>
                                    updateRoomPhotoCaption(
                                      room.id,
                                      url,
                                      e.target.value.slice(0, MAX_PHOTO_CAPTION)
                                    )
                                  }
                                  placeholder="Optional description"
                                  helperText={`${caption.length}/${MAX_PHOTO_CAPTION}`}
                                  sx={{ flex: 1, minWidth: 160 }}
                                  multiline
                                  minRows={1}
                                  maxRows={2}
                                />
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    ) : (
                      readOnly && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                          No photos for this room
                        </Typography>
                      )
                    )}

                    <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
                      Checklist
                    </Typography>
                    {(room.items || []).map((item, itemIndex) => {
                      const statusLabelId = `insp-status-${room.id}-${item.id || itemIndex}`;
                      return (
                      <Box
                        key={item.id || `${room.id}-item-${itemIndex}`}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1,
                          mb: 1.5,
                          p: 1.5,
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          bgcolor: 'background.paper'
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1,
                            flexWrap: 'wrap'
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {item.label}
                          </Typography>
                          <Chip
                            size="small"
                            label={item.status || 'pending'}
                            color={itemStatusColor(item.status)}
                            variant="outlined"
                          />
                        </Box>
                        <FormControl
                          size="small"
                          fullWidth
                          disabled={readOnly}
                          sx={{ mt: 0.5 }}
                        >
                          <InputLabel id={statusLabelId}>Status</InputLabel>
                          <Select
                            labelId={statusLabelId}
                            id={`${statusLabelId}-select`}
                            value={item.status || 'pending'}
                            label="Status"
                            onChange={(e) => updateItemStatus(room.id, item.id, e.target.value)}
                          >
                            {ITEM_STATUSES.map((s) => (
                              <MenuItem key={s.value} value={s.value}>
                                {s.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <TextField
                          size="small"
                          label="Item notes"
                          value={item.notes || ''}
                          onChange={(e) => updateItemNotes(room.id, item.id, e.target.value)}
                          disabled={readOnly}
                          fullWidth
                        />
                      </Box>
                      );
                    })}
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 2, py: 1.5 }}>
          <Button onClick={closeDetail} disabled={saving || Boolean(uploadingRoomId)}>
            {dirty && !readOnly ? 'Save & close' : 'Close'}
          </Button>
          {!readOnly && (
            <Button onClick={saveChecklist} disabled={saving} variant="outlined">
              Save checklist
            </Button>
          )}
          {active?.status === 'completed' && (
            <Button
              onClick={openSignDialog}
              disabled={saving}
              variant="contained"
              color="secondary"
              startIcon={<SignIcon />}
            >
              Send for dual signature
            </Button>
          )}
          {active?.status === 'draft' && (
            <Button
              onClick={handleStart}
              disabled={saving}
              variant="contained"
              startIcon={<StartIcon />}
            >
              Start
            </Button>
          )}
          {(active?.status === 'draft' || active?.status === 'in_progress') && (
            <Button
              onClick={handleComplete}
              disabled={saving}
              variant="contained"
              color="success"
              startIcon={<CompleteIcon />}
            >
              Mark complete
            </Button>
          )}
          {(active?.status === 'draft' || active?.status === 'in_progress') && (
            <IconButton
              onClick={handleCancel}
              disabled={saving}
              aria-label="Cancel inspection"
              color="error"
            >
              <CancelIcon />
            </IconButton>
          )}
          {saving && <CircularProgress size={22} />}
        </DialogActions>
      </Dialog>

      <Dialog open={signOpen} onClose={() => !saving && setSignOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Alberta co-signature</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Generates a fresh inspection report PDF (with photo evidence when available) and emails
            the landlord plus each tenant their own signing link. Use the newest emails after a
            resend.
          </Typography>
          <TextField
            label="Landlord name"
            fullWidth
            margin="dense"
            value={signForm.landlordName}
            onChange={(e) => setSignForm((p) => ({ ...p, landlordName: e.target.value }))}
          />
          <TextField
            label="Landlord email"
            fullWidth
            margin="dense"
            type="email"
            value={signForm.landlordEmail}
            onChange={(e) => setSignForm((p) => ({ ...p, landlordEmail: e.target.value }))}
          />
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>
            Tenant signers
          </Typography>
          {(signForm.tenants || []).map((tenantRow, idx) => (
            <Box
              key={`tenant-row-${idx}`}
              sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1.5 }}
            >
              <TextField
                label={idx === 0 ? 'Tenant name' : `Tenant ${idx + 1} name`}
                fullWidth
                margin="dense"
                value={tenantRow.name}
                onChange={(e) =>
                  setSignForm((p) => {
                    const tenants = [...(p.tenants || [])];
                    tenants[idx] = { ...tenants[idx], name: e.target.value };
                    return { ...p, tenants };
                  })
                }
              />
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                  label={idx === 0 ? 'Tenant email' : `Tenant ${idx + 1} email`}
                  fullWidth
                  margin="dense"
                  type="email"
                  value={tenantRow.email}
                  onChange={(e) =>
                    setSignForm((p) => {
                      const tenants = [...(p.tenants || [])];
                      tenants[idx] = { ...tenants[idx], email: e.target.value };
                      return { ...p, tenants };
                    })
                  }
                />
                {(signForm.tenants || []).length > 1 && (
                  <Button
                    size="small"
                    color="error"
                    sx={{ mt: 1.5, whiteSpace: 'nowrap' }}
                    onClick={() =>
                      setSignForm((p) => ({
                        ...p,
                        tenants: (p.tenants || []).filter((_, i) => i !== idx)
                      }))
                    }
                  >
                    Remove
                  </Button>
                )}
              </Box>
            </Box>
          ))}
          <Button
            size="small"
            startIcon={<AddIcon />}
            disabled={(signForm.tenants || []).length >= 6}
            onClick={() =>
              setSignForm((p) => ({
                ...p,
                tenants: [...(p.tenants || []), { name: '', email: '' }]
              }))
            }
            sx={{ mb: 1 }}
          >
            Add another tenant
          </Button>
          <TextField
            label="Message to signers"
            fullWidth
            margin="dense"
            multiline
            rows={2}
            value={signForm.message}
            onChange={(e) => setSignForm((p) => ({ ...p, message: e.target.value }))}
          />
          {signerLinks.length > 0 && (
            <Alert severity="success" sx={{ mt: 2 }}>
              Fresh signing links (use these, not older emails):
              <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {signerLinks.map((link) => (
                  <Button
                    key={link.url}
                    size="small"
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                  >
                    Open as {link.role || 'signer'} ({link.email})
                  </Button>
                ))}
              </Box>
            </Alert>
          )}
          {!signerLinks.length && signingLink && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Base signing URL ready — prefer the per-role links after send.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSignOpen(false)} disabled={saving}>
            Close
          </Button>
          <Button variant="contained" onClick={handleSendForSignature} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Send new signature invites'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InspectionManagement;
